/**
 * Prompt Detection Utilities
 * Functions to detect if user has provided prompts vs clicked "Skip for Now"
 */

import type { CopilotState } from '../data-layer/types';

/**
 * Check if user has provided prompts for a specific step
 * @param stepName - The step name to check
 * @param state - The copilot Redux state
 * @returns true if user provided prompts, false if skipped
 */
export const hasUserProvidedPrompts = (
  stepName: string,
  state: CopilotState
): boolean => {
  switch (stepName) {
    case 'Connection':
      return !!state.connectionPrompt && state.connectionPrompt.trim() !== '';

    case 'CreateAccount':
    case 'UpdateAccount':
    case 'EnableAccount':
    case 'DisableAccount':
    case 'RemoveAccount':
      return (
        !!state.accountPrompts[stepName] &&
        state.accountPrompts[stepName].trim() !== ''
      );

    case 'AddAccess':
      return Object.keys(state.addAccessPrompts || {}).length > 0;

    case 'RemoveAccess':
      return Object.keys(state.removeAccessPrompts || {}).length > 0;

    default:
      return false;
  }
};

/**
 * Check if user has provided prompts for any step
 * @param state - The copilot Redux state
 * @returns true if user provided any prompts
 */
export const hasAnyUserPrompts = (state: CopilotState): boolean => {
  // Check connection prompt
  if (state.connectionPrompt && state.connectionPrompt.trim() !== '') {
    return true;
  }

  // Check account prompts
  const accountSteps = [
    'CreateAccount',
    'UpdateAccount',
    'EnableAccount',
    'DisableAccount',
    'RemoveAccount',
  ];

  // Use array.some() instead of for...of loop
  const hasAccountPrompts = accountSteps.some(
    (step) =>
      state.accountPrompts[step] && state.accountPrompts[step].trim() !== ''
  );

  if (hasAccountPrompts) {
    return true;
  }

  // Check access prompts
  if (Object.keys(state.addAccessPrompts || {}).length > 0) {
    return true;
  }

  if (Object.keys(state.removeAccessPrompts || {}).length > 0) {
    return true;
  }

  return false;
};
